import { enableProdMode, inject, importProvidersFrom, provideExperimentalZonelessChangeDetection } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeSv from '@angular/common/locales/sv';
import localeEn from '@angular/common/locales/en';
import { environment, host } from './environments/environment';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  MAT_LUXON_DATE_ADAPTER_OPTIONS,
  LuxonDateAdapter,
  MAT_LUXON_DATE_FORMATS,
  MatLuxonDateModule,
} from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideNamedApollo, NamedOptions } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { JwtService } from './app/injectables/jwt.service';
import { split, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { setContext } from '@apollo/client/link/context';
import { PlatformModule } from '@angular/cdk/platform';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { QuillModule } from 'ngx-quill';
import { AppComponent } from './app/app.component';
import 'quill-paste-smart';

registerLocaleData(localeSv, 'sv');
registerLocaleData(localeEn, 'en');

if (environment.production) {
  enableProdMode();
}

void bootstrapApplication(AppComponent, {
  providers: [
    provideExperimentalZonelessChangeDetection(),
    importProvidersFrom(
      PlatformModule,
      BrowserModule,
      MatLuxonDateModule,
      QuillModule.forRoot({
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }],
            [{ align: [] }],
            ['link'],
          ],
        },
      }),
    ),

    {
      provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
      useValue: { firstDayOfWeek: 1 },
    },
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_LUXON_DATE_FORMATS },
    provideHttpClient(withInterceptorsFromDi()),
    provideNamedApollo(() => {
      const httpLink = inject(HttpLink);
      const jwtService = inject(JwtService);
      const link = split(
        args => args.query.definitions.some(d => d.kind === 'OperationDefinition' && d.operation === 'subscription'),
        new GraphQLWsLink(
          createClient({
            url: new URL('graphql-ws', host.clientUrl).href,
            lazy: true,
            shouldRetry: () => true,
            retryAttempts: Infinity,
            connectionParams: () => ({
              headers: { authorization: `Bearer ${jwtService.jwt}` },
            }),
          }),
        ),
        ApolloLink.from([
          setContext(() => ({
            headers: { Authorization: `Bearer ${jwtService.jwt}` },
          })),
          httpLink.create({
            uri: new URL('/graphql', host.clientUrl).href,
          }),
        ]),
      );
      const cache = new InMemoryCache();
      return {
        default: {
          cache,
          link,
          connectToDevTools: !environment.production,
        },
        auth: {
          cache,
          link: ApolloLink.from([
            setContext(() => ({
              headers: { Authorization: `Bearer ${jwtService.jwt}` },
            })),
            httpLink.create({
              uri: new URL('/auth-service/graphql', host.clientUrl).href,
            }),
          ]),
          connectToDevTools: !environment.production,
        },
      } satisfies NamedOptions;
    }),
    provideAnimations(),
  ],
});
